// IntentsPopup.js
import React, { useState, useEffect, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS } from 'chart.js/auto';

// Define the stackedBarLabelsPlugin
const stackedBarLabelsPlugin = {
  id: 'stackedBarLabelsPlugin',
  afterDatasetsDraw(chart) {
    const { ctx, data, scales, options } = chart;
    const percentageChanges = options.plugins.percentageChangePlugin.percentageChanges;
    const currentCounts = options.plugins.percentageChangePlugin.currentCounts;
    const baselineCounts = options.plugins.percentageChangePlugin.baselineCounts;

    data.datasets[0].data.forEach((value, index) => {
      const total = data.datasets.reduce((sum, dataset) => sum + dataset.data[index], 0);
      const xStart = scales.x.getPixelForValue(0);
      const xEnd = scales.x.getPixelForValue(total);
      const barWidth = xEnd - xStart;
      //console.log(total, barWidth, xStart, xEnd)
      let barHeight = 20;
      if(data.labels.length > index+1) {
        barHeight = scales.y.getPixelForTick(index + 1) - scales.y.getPixelForTick(index);
      } else if(index >= 1) {
        barHeight = scales.y.getPixelForTick(index) - scales.y.getPixelForTick(index-1);
      }
      const yPos = scales.y.getPixelForValue(data.labels[index]) + barHeight / 2 - 14;

      ctx.save();
      ctx.textBaseline = 'middle';
      ctx.font = '12px Lato';

      const percentageChange = parseFloat(percentageChanges[index]);
      const currentCount = currentCounts[index];
      let displayText = `${percentageChange.toFixed(1)}% (${currentCount})`;
      if(baselineCounts[index] == 0) {
        displayText = `* NEW * (${currentCount})`;
      }
      console.log(baselineCounts[index])

      if (barWidth > 200 && total > 0) {
        ctx.textAlign = 'center';
        ctx.fillStyle = 'black';
        ctx.fillText(displayText, (xStart + xEnd) / 2, yPos);
      } else {
        ctx.textAlign = 'left';
        ctx.fillStyle = 'black';
        ctx.fillText(displayText, Math.max(xEnd + 5, xStart + 5), yPos);
      }
      //console.log(displayText, data.labels[index], scales.y.getPixelForValue(data.labels[index]),  yPos)

      ctx.restore();
    });
  },
};

const IntentsPopup = ({ intentCounts, baselines, onClose, baselineMultiplier }) => {
  const [sortBy, setSortBy] = useState('volume');
  const [searchQuery, setSearchQuery] = useState('');
  const [intentsData, setIntentsData] = useState([]);
  const [baselineData, setBaselineData] = useState([]);
  const [deltaData, setDeltaData] = useState([]);
  const [deltaBackgroundColors, setDeltaBackgroundColors] = useState([]);
  const [percentageChanges, setPercentageChanges] = useState([]);
  const [currentCounts, setCurrentCounts] = useState([]);
  const [baselineCounts, setBaselineCounts] = useState([]);
  const chartRef = useRef(null);

  console.log("Intent Popup Mult:", baselineMultiplier);

  useEffect(() => {
    const data = Object.entries(intentCounts).map(([intent, count]) => {
      const baselineCount = baselines.avgIntentFrequencies[intent] * baselineMultiplier || 0;
      const currentCount = count;

      const minCount = Math.min(currentCount, baselineCount);
      const change = Math.abs(currentCount - baselineCount);

      const isIncrease = currentCount >= baselineCount;
      const deltaColor = isIncrease ? 'rgba(40, 235, 40, 0.6)' : 'rgba(235, 50, 50, 0.5)';

      let percentageChange = 0;
      if (baselineCount !== 0) {
        percentageChange = ((currentCount - baselineCount) / baselineCount) * 100;
      } else if (currentCount !== 0) {
        percentageChange = 100;
      }

      return { intent, currentCount, baselineCount, percentageChange, minCount, change, deltaColor };
    });

    // Apply search filter
    const filteredData = data.filter((item) =>
      item.intent.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Apply sorting
    filteredData.sort((a, b) =>
      sortBy === 'volume' ? b.currentCount - a.currentCount : b.percentageChange - a.percentageChange
    );

    setIntentsData(filteredData);

    // Update datasets
    setBaselineData(filteredData.map((item) => item.minCount));
    setDeltaData(filteredData.map((item) => item.change));
    setDeltaBackgroundColors(filteredData.map((item) => item.deltaColor));
    setPercentageChanges(filteredData.map((item) => item.percentageChange));
    setCurrentCounts(filteredData.map((item) => item.currentCount));
    setBaselineCounts(filteredData.map((item) => item.baselineCount));
  }, [intentCounts, baselines, sortBy, searchQuery]);

  // Chart data and options based on the sorting method
  let chartData, chartOptions, chartPlugins;

  if (sortBy === 'volume') {
    chartData = {
      labels: intentsData.map((item) => item.intent),
      datasets: [
        {
          label: 'Baseline',
          data: baselineData,
          backgroundColor: 'rgba(99, 102, 241, 0.8)',
          stack: 'Stack 0',
          tooltip: { enabled: false },
        },
        {
          label: 'Change',
          data: deltaData,
          backgroundColor: deltaBackgroundColors,
          stack: 'Stack 0',
        },
      ],
    };

    chartOptions = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          mode: 'nearest',
          intersect: false,
          axis: 'y',
          filter: function (tooltipItem) {
            return tooltipItem.datasetIndex === 1;
          },
          callbacks: {
            title: function (tooltipItems) {
              if(tooltipItems && tooltipItems.length) {
                return tooltipItems[0].label;
              }
              return "";
            },
            label: function(context) {
              const index = context.dataIndex;;
              const percentChange = parseFloat(percentageChanges[index]).toFixed(1);
              const currentCount = Math.round(currentCounts[index]);
              const averageCount = parseFloat(baselineCounts[index]).toFixed(1);
              return [
                `Current: ${currentCount}`,
                `Average: ${averageCount}`,
                `Change: ${percentChange >= 0 ? '+' : ''}${percentChange}%`
              ];
            }
          },
        },
        percentageChangePlugin: {
          percentageChanges,
          currentCounts,
          baselineCounts,
        },
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          title: {
            display: true,
            text: 'Count',
          },
        },
        y: {
          stacked: true,
          title: { display: false },
          ticks: {
            autoSkip: false,
          },
        },
      },
    };

    chartPlugins = [stackedBarLabelsPlugin];
  } else {
    // For percentage change sorting
    chartData = {
      labels: intentsData.map((item) => item.intent),
      datasets: [
        {
          label: 'Percentage Change',
          data: intentsData.map((item) => item.percentageChange),
          backgroundColor: intentsData.map((item) =>
            item.percentageChange >= 0 ? 'rgba(40, 235, 40, 0.6)' : 'rgba(235, 50, 50, 0.5)'
          ),
        },
      ],
    };

    const minPercentageChange = Math.min(...percentageChanges);
    const maxPercentageChange = Math.max(...percentageChanges);

    chartOptions = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        filter: function (tooltipItem) {
          return tooltipItem.datasetIndex === 1;
        },
        tooltip: {
          callbacks: {
            title: function (tooltipItems) {
              return tooltipItems[0].label;
            },
            
            label: function(context) {
              const index = context.dataIndex;;
              const percentChange = parseFloat(percentageChanges[index]).toFixed(1);
              const currentCount = Math.round(currentCounts[index]);
              const averageCount = parseFloat(baselineCounts[index]).toFixed(1);
              return [
                `Current: ${currentCount}`,
                `Average: ${averageCount}`,
                `Change: ${percentChange >= 0 ? '+' : ''}${percentChange}%`
              ];
            },
          },
        },
        percentageChangePlugin: {
          percentageChanges,
          currentCounts,
          baselineCounts,
        },
      },
      scales: {
        x: {
          beginAtZero: false,
          min: minPercentageChange - 5,
          max: maxPercentageChange + 5,
          title: {
            display: true,
            text: 'Percentage Change',
          },
        },
        y: {
          title: { display: false },
          ticks: {
            autoSkip: false,
          },
        },
      },
    };

    chartPlugins = []; // No need for stackedBarLabelsPlugin in this view
  }

  // Adjust chart height based on the number of data points
  const chartHeight = Math.max(intentsData.length * 30, 150);

  console.log("Chart Height:", chartHeight)

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3 text-center">
          <h3 className="text-lg leading-6 font-medium text-gray-900">All Intents</h3>
          <div className="mt-2 px-7 py-3">
            <div className="flex justify-between items-center mb-4">
              <div>
                <button
                  className={`px-4 py-2 rounded ${
                    sortBy === 'volume' ? 'bg-indigo-500 text-white' : 'bg-gray-200'
                  }`}
                  onClick={() => setSortBy('volume')}
                >
                  Sort by Volume
                </button>
                <button
                  className={`px-4 py-2 rounded ml-2 ${
                    sortBy === 'percentage' ? 'bg-indigo-500 text-white' : 'bg-gray-200'
                  }`}
                  onClick={() => setSortBy('percentage')}
                >
                  Sort by Breakout
                </button>
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Search intents"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="px-4 py-2 border rounded"
                />
              </div>
            </div>
            <div style={{ height: '70vh', overflow: 'auto' }}>
              <div style={{ height: chartHeight, overflow: 'hidden' }}>
                <div style={{ minHeight: '100%', overflow: 'hidden' }}>
                  <Bar
                    ref={chartRef}
                    data={chartData}
                    options={chartOptions}
                    plugins={chartPlugins}
                    height={chartHeight}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="items-center px-4 py-3">
            <button
              id="ok-btn"
              className="px-4 py-2 bg-indigo-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntentsPopup;
